/**
 * TripListItem - Individual trip row for the TripListPanel
 *
 * Renders a single trip with:
 * - 3-state cycling toggle icon (Unlocked → Locked → Excluded)
 * - Aircraft info (tail number, make/model)
 * - Time range
 * - Current/proposed spot assignment
 * - Conflict indicators
 */

import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import BlockIcon from "@mui/icons-material/Block";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  alpha,
  Button,
  Chip,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { GroupedTrip, TripParkingState } from "../types";

/** Size badge configuration */
export interface SizeBadge {
  label: string;
  color: string;
  tooltip: string;
}

/** Move info from impact summary */
export interface MoveInfo {
  trip_id: string;
  toSpot?: string;
}

/** Conflict info from impact summary */
export interface ConflictInfo {
  trip_id: string;
  reason: string;
}

/** Available location for assignment */
export interface AvailableLocation {
  id: string;
  name: string;
  type: "hangar" | "ramp";
}

export interface TripListItemProps {
  trip: GroupedTrip;
  tripState: TripParkingState;
  index: number;
  isFocused: boolean;
  moveInfo: MoveInfo | null;
  conflictInfo: ConflictInfo | null;
  sizeBadge: SizeBadge | null;
  formattedTimeRange: string;
  showDetailedView: boolean;
  onCycleTripState: (tripId: string) => void;
  onTripHover?: (tripId: string | null) => void;
  // Assignment props (for unplaced aircraft)
  availableLocations?: AvailableLocation[];
  onAssignToLocation?: (
    tripId: string,
    locationId: string,
    locationType: "hangar" | "ramp",
    locationName: string
  ) => void;
}

/**
 * Get the appropriate icon for the current trip state
 */
function getStateIcon(tripState: TripParkingState): React.ReactElement {
  switch (tripState) {
    case "locked":
      return <LockIcon />;
    case "excluded":
      return <BlockIcon />;
    default:
      return <LockOpenOutlinedIcon />;
  }
}

/**
 * Get the color for the state icon button
 */
function getStateColor(tripState: TripParkingState): "success" | "default" | "primary" {
  switch (tripState) {
    case "locked":
      return "success";
    case "excluded":
      return "default";
    default:
      return "primary";
  }
}

export const TripListItem: React.FC<TripListItemProps> = ({
  trip,
  tripState,
  index,
  isFocused,
  moveInfo,
  conflictInfo,
  sizeBadge,
  formattedTimeRange,
  showDetailedView,
  onCycleTripState,
  onTripHover,
  availableLocations,
  onAssignToLocation,
}) => {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const isLocked = tripState === "locked";
  const isExcluded = tripState === "excluded";

  // Filter locations into hangars and ramps
  const hangars = availableLocations?.filter((loc) => loc.type === "hangar") || [];
  const ramps = availableLocations?.filter((loc) => loc.type === "ramp") || [];

  const getStateTooltip = () => {
    switch (tripState) {
      case "locked":
        return t("screens.ramp.autoPark.tripList.tooltips.clickToExclude");
      case "excluded":
        return t("screens.ramp.autoPark.tripList.tooltips.clickToInclude");
      default:
        return t("screens.ramp.autoPark.tripList.tooltips.clickToLock");
    }
  };

  return (
    <ListItem
      data-trip-index={index}
      disablePadding
      onMouseEnter={() => onTripHover?.(trip.trip_id)}
      onMouseLeave={() => onTripHover?.(null)}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        // Focus indicator: blue left border
        borderLeft: isFocused ? 4 : 0,
        borderLeftColor: "primary.main",
        bgcolor: isFocused
          ? (theme) => alpha(theme.palette.primary.light, 0.08)
          : isLocked
          ? (theme) => alpha(theme.palette.success.light, 0.1)
          : conflictInfo
          ? (theme) => alpha(theme.palette.error.light, 0.1)
          : "transparent",
        // Dim excluded rows
        opacity: isExcluded ? 0.5 : 1,
        transition: "all 0.15s ease-in-out",
      }}
    >
      <ListItemButton onClick={() => onCycleTripState(trip.trip_id)} dense>
        {/* 3-state cycling icon button */}
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Tooltip title={getStateTooltip()}>
            <IconButton
              size="small"
              color={getStateColor(tripState)}
              onClick={(e) => {
                e.stopPropagation();
                onCycleTripState(trip.trip_id);
              }}
            >
              {getStateIcon(tripState)}
            </IconButton>
          </Tooltip>
        </ListItemIcon>
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center">
              {showDetailedView && sizeBadge && (
                <Tooltip title={sizeBadge.tooltip}>
                  <Chip
                    label={sizeBadge.label}
                    size="small"
                    sx={{
                      height: 18,
                      minWidth: 24,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      bgcolor: sizeBadge.color,
                      color: "white",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                </Tooltip>
              )}
              {trip.tail_number && (
                <Chip
                  label={trip.tail_number}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                  }}
                />
              )}
              <Typography variant="body2" noWrap>
                {trip.aircraft_model ||
                  trip.aircraft_make ||
                  t("screens.ramp.autoPark.tripList.unknownAircraft")}
              </Typography>
            </Stack>
          }
          primaryTypographyProps={{ component: "div" }}
          secondary={
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                {formattedTimeRange}
              </Typography>
              {trip.current_spot_name && (
                <Typography variant="caption" color="text.secondary">
                  {t("screens.ramp.autoPark.tripList.current")} {trip.current_spot_name}
                </Typography>
              )}
              {/* Show proposed spot in preview mode */}
              {moveInfo && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <SwapHorizIcon sx={{ fontSize: 14, color: "warning.main" }} />
                  <Typography variant="caption" color="warning.main">
                    {moveInfo.toSpot}
                  </Typography>
                </Stack>
              )}
              {/* Show conflict info with assign option */}
              {conflictInfo && (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="error.main">
                    {conflictInfo.reason}
                  </Typography>
                  {onAssignToLocation && availableLocations && availableLocations.length > 0 && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddLocationAltIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setMenuAnchorEl(e.currentTarget as HTMLElement);
                        }}
                        sx={{
                          minHeight: 0,
                          py: 0.25,
                          px: 1,
                          fontSize: "0.75rem",
                          alignSelf: "flex-start",
                        }}
                      >
                        {t("screens.ramp.autoPark.tripList.assign")}
                      </Button>
                      <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={() => setMenuAnchorEl(null)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hangars.length > 0 && (
                          <ListSubheader sx={{ lineHeight: "32px" }}>
                            {t("screens.ramp.autoPark.tripList.hangarsSection")}
                          </ListSubheader>
                        )}
                        {hangars.map((location) => (
                          <MenuItem
                            key={location.id}
                            onClick={() => {
                              onAssignToLocation(
                                trip.trip_id,
                                location.id,
                                location.type,
                                location.name
                              );
                              setMenuAnchorEl(null);
                            }}
                          >
                            {location.name}
                          </MenuItem>
                        ))}
                        {ramps.length > 0 && (
                          <ListSubheader sx={{ lineHeight: "32px" }}>
                            {t("screens.ramp.autoPark.tripList.rampsSection")}
                          </ListSubheader>
                        )}
                        {ramps.map((location) => (
                          <MenuItem
                            key={location.id}
                            onClick={() => {
                              onAssignToLocation(
                                trip.trip_id,
                                location.id,
                                location.type,
                                location.name
                              );
                              setMenuAnchorEl(null);
                            }}
                          >
                            {location.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      </ListItemButton>
    </ListItem>
  );
};
